/**
 * What the composer SAYS happened, separate from anyone's decision to record it (ui#1973).
 *
 * Owner directive, 2026-08-27: business logic emits events, telemetry subscribes. `CreateFlow` names one
 * fact — a send began — and moves on; it does not know who is listening, or whether anyone is. The same
 * split `download/events/downloadEvents.ts` made for the download paths, for the same reason: without
 * it, measuring the recipient funnel would mean editing the submit path, which is the code least safe to
 * touch, and a reader of that path could not tell which lines were the product and which the instrument.
 *
 * ── Why "started" and nothing else ───────────────────────────────────────────────────────────────
 * One name, because one fact is currently needed and a vocabulary invented ahead of its consumers is a
 * vocabulary nobody can rename later. Completion is deliberately absent: the composer already reports
 * completed sends through the offer funnel's post-send moment, and a second completion counter would be
 * a worse copy of one that exists.
 *
 * The name is the DOMAIN's, not any subscriber's. `recipient.send.first` is what the recipient funnel
 * makes of this event; that mapping lives in the subscriber, so the wire vocabulary can change without
 * the submit path being reopened, which is the whole point of the split.
 *
 * React-free by mandate (`logic-outside-react`).
 */

/** The one thing the composer can say about a send today. */
export type SendEventName = "started";

/** One thing that happened in the composer. No payload: nothing a subscriber needs is per-send. */
export interface SendEvent {
	name: SendEventName;
}

export type SendEventListener = (event: SendEvent) => void;

export type Unsubscribe = () => void;

/** The seam the composer holds: one thing it can say, and a way for others to listen. */
export interface SendEventEmitter {
	/**
	 * A send began — every refusal has already been passed and the flow is committing to the transfer.
	 *
	 * Named at commit rather than at submit on purpose: an over-cap selection with no allowance, or a
	 * sender-address mismatch, returns before this point, and a funnel that counted refused attempts as
	 * sends would report conversions that never happened.
	 */
	started(): void;
	subscribe(listener: SendEventListener): Unsubscribe;
}

/**
 * A module singleton, like the personalization and offer emitters.
 *
 * `CreateFlow` is mounted once and kept alive across the landing and the four destination sheets, but
 * its subscribers are wired on the shell, which outlives even that. A per-instance emitter would make
 * the wiring depend on mount order for no gain.
 */
export function createSendEventEmitter(): SendEventEmitter {
	const listeners = new Set<SendEventListener>();

	return {
		started() {
			// Iterated over a copy so a listener may unsubscribe from inside its own callback without
			// skipping the listener that follows it.
			[...listeners].forEach(listener => listener({name: "started"}));
		},
		subscribe(listener) {
			listeners.add(listener);
			return () => {
				listeners.delete(listener);
			};
		},
	};
}

/** The app's emitter. */
export const sendEvents: SendEventEmitter = createSendEventEmitter();
